/**
 * StudentPlay.tsx
 * Single student "play" view for a continuous session. Subscribes to the
 * room and renders the answer screen matching whatever activity the teacher
 * is currently presenting, so students automatically follow along as the
 * teacher moves through slides and switches tools.
 */

import React, { useState, useEffect } from 'react';
import { subscribeToRoom } from '@db/roomService';
import { ensureAnonymousAuth } from '@db/firebase';
import type { Room } from '@types-local/room.types';
import AnswerMC from './AnswerMC';
import AnswerWordCloud from './AnswerWordCloud';
import AnswerShortText from './AnswerShortText';
import AnswerDraw from './AnswerDraw';
import AnswerMedia from './AnswerMedia';

export default function StudentPlay(): React.ReactElement {
  const [room, setRoom] = useState<Room | null>(null);
  const [loaded, setLoaded] = useState(false);
  const roomCode = sessionStorage.getItem('gh_room_code') ?? '';
  const studentName = sessionStorage.getItem('gh_student_name') ?? 'Student';

  useEffect(() => {
    ensureAnonymousAuth();
    if (!roomCode) return;
    return subscribeToRoom(roomCode, (r) => { setRoom(r); setLoaded(true); });
  }, [roomCode]);

  // Session ended (teacher clicked End) or the room no longer exists.
  if (loaded && (room === null || room.status === 'closed')) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center space-y-4">
        <div className="text-6xl">👋</div>
        <h2 className="text-xl font-bold text-slate-900">Class session ended</h2>
        <p className="text-sm text-slate-500">Thanks for joining, {studentName}!</p>
      </div>
    );
  }

  // Each answer component subscribes to the room itself; this wrapper only
  // decides which one to show based on the active activity type.
  const type = room?.activeConfig?.type ?? null;

  switch (type) {
    case 'multiple_choice': return <AnswerMC />;
    case 'word_cloud':      return <AnswerWordCloud />;
    case 'short_answer':    return <AnswerShortText />;
    case 'slide_drawing':   return <AnswerDraw />;
    case 'media_upload':    return <AnswerMedia />;
    default:
      // No active question — mirror the current slide so students follow along.
      if (room?.slideImageUrl) {
        return (
          <div className="min-h-screen flex flex-col items-center justify-center bg-black p-2">
            <img
              src={room.slideImageUrl}
              alt="Current slide"
              className="max-w-full max-h-screen rounded-lg object-contain"
            />
          </div>
        );
      }
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center space-y-4">
          <div className="text-5xl animate-bounce">⏳</div>
          <h2 className="text-xl font-bold text-slate-900">Waiting for teacher…</h2>
          <p className="text-sm text-slate-500">Hi {studentName}! Your teacher will start an activity soon.</p>
          <p className="text-xs font-mono text-brand-400">{roomCode}</p>
        </div>
      );
  }
}
